import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield } from 'lucide-react';

const POSTHOG_KEY = 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const POSTHOG_HOST = 'https://eu.i.posthog.com'; // EU-Region für DSGVO

// Posthog dynamisch laden — nur nach Cookie-Consent
function loadPosthog() {
  if (window.posthog && window.posthog.__loaded) return;
  // Idempotent: nur einmal laden
  if (window.__enbet_posthog_loading) return;
  window.__enbet_posthog_loading = true;

  // Posthog Loader-Snippet (offizieller Standard) — inline minimal
  /* eslint-disable */
  !(function (t, e) {
    var o, n, p, r;
    e.__SV ||
      ((window.posthog = e),
        (e._i = []),
        (e.init = function (i, s, a) {
          function g(t, e) {
            var o = e.split('.');
            2 == o.length && ((t = t[o[0]]), (e = o[1])),
              (t[e] = function () {
                t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
              });
          }
          ((p = t.createElement('script')).type = 'text/javascript'),
            (p.crossOrigin = 'anonymous'),
            (p.async = !0),
            (p.src = s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js'),
            (r = t.getElementsByTagName('script')[0]).parentNode.insertBefore(p, r);
          var u = e;
          for (
            void 0 !== a ? (u = e[a] = []) : (a = 'posthog'),
            u.people = u.people || [],
            u.toString = function (t) {
              var e = 'posthog';
              return 'posthog' !== a && (e += '.' + a), t || (e += ' (stub)'), e;
            },
            u.people.toString = function () {
              return u.toString(1) + '.people (stub)';
            },
            o = 'init capture identify reset register'.split(' '),
            n = 0;
            n < o.length;
            n++
          )
            g(u, o[n]);
          e._i.push([i, s, a]);
        }),
        (e.__SV = 1));
  })(document, window.posthog || []);
  /* eslint-enable */

  window.posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    autocapture: true,
    capture_pageview: true,
    disable_session_recording: false,
    session_recording: {
      recordCrossOriginIframes: false,
      capturePerformance: false,
    },
    // DSGVO: Respect-DoNotTrack-Header
    respect_dnt: true,
  });
}

function unloadPosthog() {
  // Auf Opt-Out: bestehende Cookies entfernen + stop tracking
  if (window.posthog && typeof window.posthog.opt_out_capturing === 'function') {
    try { window.posthog.opt_out_capturing(); } catch {}
  }
}

export default function CookieConsent({ onPrivacy }) {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('enbet_cookie_consent');
    if (consent === 'accepted') {
      // User hat schon mal zugestimmt → Posthog direkt laden
      loadPosthog();
    } else if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('enbet_cookie_consent', 'accepted');
    loadPosthog();
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem('enbet_cookie_consent', 'declined');
    unloadPosthog();
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cookie-consent-banner"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 left-0 right-0 z-[90] bg-[#0a0a0a] border-t border-[#333] px-6 py-5"
        >
          <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Shield size={20} className="text-[#befa0a] mt-0.5 flex-shrink-0" />
              <div>
                <p
                  className="text-white text-sm font-bold mb-1"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  {t('cookie.headline')}
                </p>
                <p
                  className="text-white/50 text-xs leading-relaxed"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {t('cookie.text')}{' '}
                  <button
                    data-testid="cookie-privacy-link"
                    onClick={onPrivacy}
                    className="text-[#befa0a] underline underline-offset-2 hover:text-white transition-colors"
                  >
                    {t('cookie.privacy')}
                  </button>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                data-testid="cookie-decline-btn"
                onClick={handleDecline}
                className="text-white/50 text-xs uppercase tracking-[0.15em] px-5 py-2.5 border border-[#333] hover:border-white/40 transition-colors"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {t('cookie.decline')}
              </button>
              <button
                data-testid="cookie-accept-btn"
                onClick={handleAccept}
                className="text-[#0a0a0a] text-xs uppercase tracking-[0.15em] px-5 py-2.5 bg-[#befa0a] hover:bg-white transition-colors font-bold"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {t('cookie.accept')}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
